// types
import { Loan } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Chip, Grid, LinearProgress, Paper, SxProps, Typography } from '@mui/material';

// theme
import { theme } from '../../../theme';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';

// props
export interface CardLoanLiteProps {
	hasCurrency?: boolean;
	isActive?: boolean;
	loan: Loan;
	onClick: () => void;
}

export const CardLoanLite = ({ hasCurrency, isActive, loan, onClick }: CardLoanLiteProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const availableFunds = Number((loan.amount - loan.disbursed).toFixed(0));
	const percent = Number(((availableFunds / loan.amount) * 100).toFixed(0));

	return (
		<Paper
			onClick={onClick}
			sx={(theme) => {
				let sx: SxProps = {
					borderColor: 'transparent',
					borderStyle: 'solid',
					borderWidth: 3,
					padding: theme.spacing(2),
					width: '100%',
					height: '100%',
					cursor: 'pointer',
				};

				if (isActive) {
					sx = {
						...sx,
						borderColor: theme.palette.brand.blue[500],
					};
				}
				return sx;
			}}>
			<Grid
				container
				direction="column"
				justifyContent="space-between"
				spacing={2}
				sx={{ height: `calc(100% + ${theme.spacing(3)})` }}>
				<Grid item>
					<Grid alignItems="center" container justifyContent="space-between" spacing={2}>
						<Grid item>
							<Chip className="tag" color="teal" label={loan.code} variant="twoTone" />
						</Grid>
						<Grid item>
							<Grid alignItems="center" container spacing={2}>
								<Grid item>
									<Grid alignItems="center" container spacing={1}>
										<Grid item>
											<Typography variant="caption" color="text.secondary">
												{t('closing')}
											</Typography>
										</Grid>
										<Grid item>
											<Typography fontWeight={600} variant="caption">
												{formatDate({ date: loan.dateClosing, fallback: t('na') })}
											</Typography>
										</Grid>
									</Grid>
								</Grid>
								{hasCurrency && (
									<Grid item>
										<Grid container spacing={1}>
											<Grid item>
												<Typography color="text.secondary" variant="caption">
													{t('currency')}
												</Typography>
											</Grid>
											<Grid item>
												<Typography fontWeight={600} variant="caption">
													{loan.currency}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<Grid item>
					<Grid container>
						<Grid item xs={12}>
							<Typography color="text.secondary" variant="caption">
								{t('undisbursed')}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<Typography color="brand.blue.500" fontWeight={600} variant="h2">
								{formatCurrency({
									currency: loan.currency,
									value: availableFunds,
								})}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<Grid alignItems="center" container spacing={1}>
								<Grid item xs={true}>
									<LinearProgress
										variant="determinate"
										value={percent}
										sx={{
											height: 7,
											borderRadius: 4,
											bgcolor: '#f5f5f5',
											'& .MuiLinearProgress-bar': {
												backgroundColor: theme.palette.brand.blue[500],
											},
										}}
									/>
								</Grid>
								<Grid item xs="auto">
									<Typography color="text.secondary" fontWeight={600} sx={{ ml: 0 }}>
										{percent}%
									</Typography>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
};
